import { useState } from 'react'
import { motion } from 'framer-motion'
import { Mail, Phone, Send, Instagram, Linkedin } from 'lucide-react'
import { contactInfo } from '../lib/data.ts'
import PrivacyPolicyModal from './PrivacyPolicyModal'
import { fadeInUp, staggerContainer, staggerItem } from '../lib/motion.ts'
import SectionTitle from './SectionTitle.tsx'

interface FormData {
  name: string
  email: string
  message: string
}

interface FormErrors {
  name?: string
  email?: string
  message?: string
}

const Contact = () => {
  const [showPrivacy, setShowPrivacy] = useState(false)
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    message: ''
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required'
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    setIsSubmitting(true)
    
    // Simulate form submission
    await new Promise(resolve => setTimeout(resolve, 2000))
    
    setIsSubmitted(true)
    setIsSubmitting(false)
    
    // Reset form after success
    setTimeout(() => {
      setFormData({ name: '', email: '', message: '' })
      setIsSubmitted(false)
    }, 3000)
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({ ...prev, [name]: undefined }))
    }
  }

  const socialIcons = {
    instagram: Instagram,
    linkedin: Linkedin,
  }

  return (
    <section id="contact" className="section-padding bg-gray-900 text-white">
      <div className="container">
        <SectionTitle
          subtitle="Contact"
          title="Let's Build Amazing Together."
          colorStyle="light"
          description="Ready to get started? We'd love to hear about your project and discuss how we can help bring your vision to life."
          className="text-white [&_span]:text-accent [&_p]:text-gray-300"
        />

        <div className="mt-16 lg:mt-20">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              <motion.h3 
                variants={staggerItem}
                className="text-2xl font-display font-bold mb-8"
              >
                Get in touch
              </motion.h3>

              <motion.div variants={staggerContainer} className="space-y-6 mb-8">
                <motion.div variants={staggerItem} className="flex items-center">
                  <div className="bg-accent/20 rounded-lg p-3 mr-4">
                    <Mail size={20} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">Email</p>
                    <a 
                      href={`mailto:${contactInfo.email}`}
                      className="text-white hover:text-accent transition-colors"
                    >
                      {contactInfo.email}
                    </a>
                  </div>
                </motion.div>

                <motion.div variants={staggerItem} className="flex items-center">
                  <div className="bg-accent/20 rounded-lg p-3 mr-4">
                    <Phone size={20} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-gray-300 text-sm">Phone</p>
                    <a 
                      href={`tel:${contactInfo.phone}`}
                      className="text-white hover:text-accent transition-colors"
                    >
                      {contactInfo.phone}
                    </a>
                  </div>
                </motion.div>
              </motion.div>

              {/* Social Links */}
              <motion.div variants={staggerItem}>
                <h4 className="font-display font-bold mb-4">Follow Us</h4>
                <div className="flex gap-4">
                  {Object.entries(contactInfo.social).map(([platform, url]) => {
                    const Icon = socialIcons[platform as keyof typeof socialIcons]
                    if (!Icon) return null
                    
                    return (
                      <motion.a
                        key={platform}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-white/10 rounded-lg p-3 hover:bg-accent transition-colors group"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Icon size={20} className="text-white group-hover:text-white" />
                      </motion.a>
                    )
                  })}
                </div>
              </motion.div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial="initial"
              whileInView="animate"
              viewport={{ once: true }}
              variants={fadeInUp}
            >
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8">
                <h3 className="text-2xl font-display font-bold mb-6">
                  Start Your Project
                </h3>

                {isSubmitted ? (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="text-center py-8"
                  >
                    <div className="bg-accent/20 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <Send size={24} className="text-accent" />
                    </div>
                    <h4 className="text-xl font-display font-bold mb-2">
                      Message Sent!
                    </h4>
                    <p className="text-gray-300">
                      Thank you for reaching out. We'll get back to you within 24 hours.
                    </p>
                  </motion.div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent transition-colors ${
                          errors.name ? 'border-red-400' : 'border-white/20'
                        }`}
                        placeholder="Your full name"
                      />
                      {errors.name && (
                        <p className="text-red-400 text-sm mt-1">{errors.name}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-sm font-medium mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent transition-colors ${
                          errors.email ? 'border-red-400' : 'border-white/20'
                        }`}
                        placeholder="your@email.com"
                      />
                      {errors.email && (
                        <p className="text-red-400 text-sm mt-1">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-sm font-medium mb-2">
                        Project Details *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleChange}
                        className={`w-full px-4 py-3 bg-white/10 border rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-accent transition-colors resize-none ${
                          errors.message ? 'border-red-400' : 'border-white/20'
                        }`}
                        placeholder="Tell us about your project, goals, and timeline..."
                      />
                      {errors.message && (
                        <p className="text-red-400 text-sm mt-1">{errors.message}</p>
                      )}
                    </div>

                    <motion.button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full btn-accent disabled:opacity-50 disabled:cursor-not-allowed"
                      whileHover={!isSubmitting ? { scale: 1.02 } : {}}
                      whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                    >
                      {isSubmitting ? (
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                          Sending...
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          Send Message
                          <Send size={16} className="ml-2" />
                        </div>
                      )}
                    </motion.button>
                  </form>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer */}
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={fadeInUp}
          className="mt-20 pt-8 border-t border-gray-700 text-center"
        >
          <p className="text-gray-400">
            © 2025 AltDev Studios. All rights reserved.
            <span className="mx-2">|</span>
            <button
              className="underline text-accent hover:text-white transition-colors"
              onClick={() => setShowPrivacy(true)}
              type="button"
            >
              Privacy Policy
            </button>
      {showPrivacy && (
        <PrivacyPolicyModal onClose={() => setShowPrivacy(false)} />
      )}
          </p>
        </motion.div>
      </div>
    </section>
  )
}

export default Contact
